import { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { get } from 'core/libs/lodash';
import { withRouter } from 'core/libs/router';

import withUser from 'core/hocs/withUser';
import withBreakpoint from 'core/hocs/withBreakpoint';

import { createUriFromLocation, getPublisherScope, hasPreviewToken } from 'core/utils/url-helper';
import { callYaMetrikaMethod } from 'core/utils/counters-helper';
import { getCookie } from 'core/utils/cookie-helper';
import { isRamblerIdEnabled } from 'core/utils/rcm';

import requestHeadersPropTypes from 'core/utils/prop-types/request-headers';

import { USER_ID_COOKIE } from 'core/site/constants';

import codes from './codes';


function noCounterWarn(name) {
  console.warn(`Layout Builder doesn't know the counter named ${name}. Skipping it.`);
}


/**
 * @author Pavel Povalyaev <p.povalyaev@rambler-co.ru>
 */
class Counters extends PureComponent {
  static propTypes = {
    /**
     * @ignore
     */
    isMobile: PropTypes.bool,

    /**
     * @ignore
     */
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
    }).isRequired,

    /**
     * Объект конфигурации используемых счетчиков
     */
    counterIds: PropTypes.object,

    /**
     * Данные о пользователе из ramblerId
     */
    user: PropTypes.object,

  };

  static contextTypes = {
    siteName: PropTypes.string,
    ramblerId: PropTypes.shape({
      rname: PropTypes.string,
    }),
    requestHeaders: requestHeadersPropTypes,
    splitTest: PropTypes.object,
  };

  componentDidMount() {
    if (this.skip) return;

    this.setTop100FirstParty();

    /**
     * При отсутствии ramblerId пользователь будет undefined
     * и код в componentDidUpdate выполняться не будет
     */
    if (!isRamblerIdEnabled(this.context)) {
      this.setupRcm();
    }
  }

  componentDidUpdate({ location: { search: prevSearch }, user: prevUser }) {
    if (this.skip) return;

    const prevUri = createUriFromLocation(prevSearch);
    const uri = createUriFromLocation(this.props.location);
    const prevUriHadToken = hasPreviewToken(prevSearch);

    if (prevUri !== uri && !prevUriHadToken) {
      this.updateCounters(uri);
    }

    if (!isRamblerIdEnabled(this.context) || (!prevUser.resolved && this.props.user.resolved)) {
      this.setupRcm();
    }
  }

  updateCounters(uri) {
    /**
     * Если этот счётчик будет выпиливаться, нужно будет прописать
     * в ga4 отправку page_view, сейчас не отправляется там, чтобы
     * не было дублирования (https://developers.google.com/analytics/devguides/collection/ga4/views?hl=ru&client_type=gtag)
     */

    if (window.ga) {
      try {
        window.ga(() => {
          const trackers = window.ga.getAll();
          trackers.forEach(tracker => {
            const name = tracker.get('name');
            window.ga(name + '.set', 'page', uri);
            window.ga(name + '.send', 'pageview');
          });
        });
      } catch (e) {
        console.error(e);
      }
    }

    if (window.updateLiveInternetCounter) {
      window.updateLiveInternetCounter();
    }

    if (window.updateMediascopeCounter) {
      window.updateMediascopeCounter();
    }

    if (window.Ya && window.Ya.Metrika2) {
      try {
        const metrikaIds = window.Ya.Metrika2.counters().map(counter => counter.id);
        callYaMetrikaMethod(metrikaIds, 'hit', uri);
      } catch (e) {
        console.error(e);
      }
    }


    if (get(window, 'top100Counter.trackPageview')) {
      window.top100Counter.trackPageview();
    }

    [].concat(get(this, 'props.counterIds.tns', [])).forEach(({ account }) => {
      const name = 'tnsCounter' + account[0].toUpperCase() + account.slice(1);
      if (get(window, [name, 'hit'])) window[name].hit();
    });
  }

  setTop100FirstParty = () => {
    const { requestHeaders } = this.context;

    window._top100q && window._top100q.push(function() {
      window.top100Counter.updateOptions({
        pub_id: getCookie(USER_ID_COOKIE),
        pub_scope: getPublisherScope(requestHeaders.host),
      });
    });
  };

  setupRcm = () => {
    if (!window.rcm) return;

    const {
      counterIds,
      user,
    } = this.props;

    const rcmId = counterIds.rcm;
    const xuid = getCookie(USER_ID_COOKIE);
    const userId = get(user, 'userInfo.info.chain_id.default');

    window._top100q.push(function() {
      window.top100Counter.updateOptions({
        rambler_id: get(user, 'userInfo.info.chain_id.default'),
        email: get(user, 'userInfo.info.email'),
        phone: get(user, 'userInfo.info.phone'),
      });
      window.rcm('setup', {
        rcmId,
        'top100Counter': window.top100Counter,
        xuid,
        userId,
      });
    });
  };

  get skip() {
    const {
      counterIds,
      location: { search },
    } = this.props;

    return !counterIds || hasPreviewToken(search);
  }

  render() {
    if (this.skip) return null;

    const {
      counterIds,
      isMobile,
    } = this.props;

    const {
      requestHeaders: {
        'x-split': splitType,
      },
      splitTest,
      siteName,
    } = this.context;

    const splitVersions = {
      1: 'as_is',
      2: 'test',
    };

    const splitName = splitTest?.name;
    const splitDevice = isMobile ? 'mob' : 'web';
    const splitVersion = splitVersions[splitType];

    return Object.keys(counterIds).map(counterName => {
      const counterCode = codes[counterName].default || codes[counterName];

      const counterIdsArr =  [].concat(counterIds[counterName]).filter(Boolean);

      if (counterCode) {
        /**
         * Для яндекс метрики вставляем только один код.
         * Для этого передаем в код массив счетчиков а уже внутри кода
         * инициализирекм нужное кол-во раз. Также передаём параметры визита
         */
        if (counterName === 'yandexMetrika') {
          const ymParams = {};

          if (splitVersion) {
            ymParams[splitName] = {
              [splitDevice]: splitVersion,
            };
          }
          return counterCode(counterIdsArr, ymParams);
        } else if (counterName === 'top100') {
          const options = {};

          if (splitVersion) {
            options.splits = [`${splitName}_${splitDevice}_${splitVersion}`];
          }

          if (siteName) {
            options.attributes_dataset = [
              `${siteName}-desktop`,
              `${siteName}-mobile`,
            ];
          }

          return counterIdsArr.map(id => counterCode(id, options));
        }
        return counterIdsArr.map(counterCode);
      }

      return noCounterWarn(counterName);
    });
  }
}

const CountersWithHOCs = withBreakpoint(withUser(withRouter(Counters)));
CountersWithHOCs.displayName = 'Counters';

export { Counters as StorybookComponent };
export default CountersWithHOCs;
